import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, map, Observable, of } from 'rxjs';
import { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import { HttpClient } from '@angular/common/http';
import { SupabaseClientService } from '../supabase-client/supabase-client.service';
import { ENV } from '../../core-ng/injection-token';
export interface Tenants {
  name: string;
  slug: string;
  details: {
    name: string;
    size: string;
    industry: string;
    interests: string[];
  };
  created_at?: Date;
}
export interface Subscriptions {
  tenant_id: string;
  product_id: string;
  status: 'active' | 'trialing' | 'past_due';
  tier: 'free' | 'pro' | 'enterprise';
}
export interface Product {
  id: string;
  name: string;
  description: string;
  link: string;
  active: boolean;
  logo: string;
  //
  color?: string;
  logoBg?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  // statrts here
  private http = inject(HttpClient);
  private supabaseClientService = inject(SupabaseClientService);

  environment = inject(ENV);


  // 1. register ✅️
  register(
    email: string,
    password: string,
  ): Observable<{ user: User | null; session: Session | null }> {
    return from(
      this.supabaseClientService.client.auth.signUp({
        email: email,
        password: password,
      }),
    ).pipe(
      map((response) => {
        const { data, error } = response;
        if (error) {
          throw new Error(error.message);
        }
        if (data?.session?.access_token) {
          localStorage.setItem('access_token', data.session.access_token);
        }
        return data;
      }),
    );
  }
  // 2.login ✅️
  async login(user: { email: string; password: string }) {
    const email = user.email;
    const password = user.password;
    const { data, error } =
      await this.supabaseClientService.client.auth.signInWithPassword({
        email,
        password,
      });
    if (error) {
      throw new Error(error.message);
    }
    localStorage.setItem('access_token', data.session.access_token);
    const { session } = data;
    if (!session) throw new Error('No session returned from Supabase');
    return session;
  }

  // 3.create tenant n member('owner') ✅️
  createTenant(tenant: Tenants): Observable<any> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }
    const url = this.environment.consoleApi;
    return this.http.post(`${url}/supabase/create-tenant`, tenant);
  }

  // 4.getAllProducts
  getAllProducts(): Observable<Product[]> {
    return from(
      this.supabaseClientService.client
        .from('products')
        .select('*')
        .eq('active', true)
        .order('name'),
    ).pipe(
      map((response: any) => {
        if (response.error) {
          throw response.error;
        }
        return response.data as Product[];
      }),
    );
  }

  // 5.save subscription
  saveSubscriptions(subscriptionData: {
    product_id: string[];
  }): Observable<any> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }
    const url = this.environment.consoleApi;
    return this.http.post(
      `${url}/supabase/create-subscription`,
      subscriptionData,
    );
  }

  // 6.getAllSubscriptions
  getAllSubscriptions(): Observable<any[]> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }

    const url = this.environment.consoleApi;
    return this.http.get(`${url}/supabase/get-subscriptions`).pipe(
      map((response: any) => {
        return response as any[];
      }),
    );
  }

  // 7.saveProfileInfo
  updateProfile(profileData: {
    email: string;
    firstName: string;
    lastName: string;
  }): Observable<any> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }
    const url = this.environment.consoleApi;
    return this.http.post(`${url}/supabase/update-profile`, profileData);
  }

  // 7.getMyProfile
  getMyProfile(): Observable<any[]> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }

    const url = this.environment.consoleApi;
    return this.http.get(`${url}/supabase/get-my-profile`).pipe(
      map((response: any) => {
        return response as any[];
      }),
    );
  }

  // 8.resetPasssword
  resetPasssword(payload: any): Observable<any> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }
    const url = this.environment.consoleApi;
    return this.http.post(`${url}/supabase/reset-password`, payload);
  }

  // 9.createNewProject
  createNewProject(payload: any): Observable<any> {
    const url = this.environment.consoleApi;
    return this.http.post(`${url}/supabase/create-new-project`, payload);
  }

  // 10.gelAllProjects
  getAllProjects() {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found. User must be logged in.');
    }
    const url = this.environment.consoleApi;
    return this.http.get(`${url}/supabase/get-all-projects`).pipe(
      map((response: any) => {
        return response as any[];
      }),
    );
  }

  //
  //
  //
  //
  //
  //
  //

  //
  //
  private _session$ = new BehaviorSubject<Session | null>(null);
  private _user$ = new BehaviorSubject<User | null>(null);
  _sessionLoaded$ = new BehaviorSubject<boolean>(false);
  private router = inject(Router);
  constructor() {
    this.supabaseClientService.client.auth.onAuthStateChange(
      (event, session) => {
        this.handleAuthStateChange(event, session);
      },
    );
    this.loadSession();
  }
  private async handleAuthStateChange(
    event: AuthChangeEvent,
    session: Session | null,
  ) {
    if (
      (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') &&
      session?.user
    ) {
      await this.handleSignIn(session);
    } else if (event === 'SIGNED_OUT') {
      this.handleSignOut();
    }
  }

  private async handleSignIn(session: Session) {
    this._session$.next(session);
    this._user$.next(session.user);
    localStorage.setItem('isLoggedIn', 'true');
  }

  private handleSignOut() {
    this._session$.next(null);
    this._user$.next(null);
    localStorage.setItem('isLoggedIn', 'false');
  }

  async logout() {
    await this.supabaseClientService.client.auth.signOut();
  }

  private async loadSession() {
    const { data, error } =
      await this.supabaseClientService.client.auth.getSession();

    if (data?.session) {
      await this.handleSignIn(data.session);
    }

    this._sessionLoaded$.next(true);
  }

  get session$(): Observable<Session | null> {
    return this._session$.asObservable();
  }

  get user$(): Observable<User | null> {
    return this._user$.asObservable();
  }

  async sendPasswordResetEmail(email: string) {
    return this.supabaseClientService.client.auth.resetPasswordForEmail(email);
  }

  async updatePassword(newPassword: string) {
    return this.supabaseClientService.client.auth.updateUser({
      password: newPassword,
    });
  }

  removeTokens() {
    this._session$.next(null);
    this._user$.next(null);
    localStorage.setItem('isLoggedIn', 'false');
  }
  checkStorage() {
    return localStorage.getItem('isLoggedIn') == 'true' ? true : false;
  }

  relogin() {
    this.handleSignOut();
    this.router.navigate(['/login'], {
      queryParams: {
        isExpired: '1',
        url: location.pathname,
      },
    });
    return of(null);
  }

  async refresh() {
    try {
      const { data, error } =
        await this.supabaseClientService.client.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        throw new Error(error.message);
      }
      const session = data?.session;
      if (!session) {
        throw new Error('No session returned from refresh.');
      }
      console.log('Session refreshed successfully.');
      return session;
    } catch (err: any) {
      console.error('Refresh failed:', err.message);
      await this.logout();
      throw err;
    }
  }
}
